import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Icon } from '@iconify/react';

import { Spinner } from '@core/components/ui';

/**
 * Superuser-only picker to enter any tenant as a guest. Presentational: the
 * caller owns the organizations query and the impersonate mutation, so this
 * only reports the chosen uuid and shows an inline error when that rejects.
 */
export function ImpersonateTenantModal({ isOpen, onClose, onImpersonate, organizations = [], isLoading, isSubmitting }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setError(null);
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return organizations;
    return organizations.filter((organization) => organization.name.toLowerCase().includes(query));
  }, [organizations, search]);

  if (!isOpen) return null;

  const handlePick = async (organization) => {
    if (isSubmitting) return;
    setError(null);
    try {
      await onImpersonate(organization.uuid);
      onClose();
    } catch {
      setError(t('workspace.impersonate.failed', { name: organization.name }));
    }
  };

  return (
    <div
      className="fixed inset-0 z-80 flex items-start justify-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px] md:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="impersonate-tenant-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-[var(--color-border)] bg-white p-7 shadow-2xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-amber-600">
              {t('workspace.impersonate.eyebrow')}
            </p>
            <h2 id="impersonate-tenant-title" className="text-xl font-semibold text-[var(--color-text)]">
              {t('workspace.impersonate.title')}
            </h2>
            <p className="mt-2 text-sm leading-6 text-[var(--color-text-secondary)]">
              {t('workspace.impersonate.body')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('workspace.impersonate.close')}
            className="cursor-pointer flex size-9 shrink-0 items-center justify-center rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-muted)]"
          >
            <Icon icon="mdi:close" className="size-5" />
          </button>
        </div>

        <label htmlFor="impersonate-tenant-search" className="sr-only">{t('workspace.impersonate.searchLabel')}</label>
        <input
          ref={inputRef}
          id="impersonate-tenant-search"
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('workspace.impersonate.searchPlaceholder')}
          className="w-full rounded-xl border border-[var(--color-border)] px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
          onKeyDown={(event) => {
            if (event.key === 'Escape') onClose();
          }}
        />
        {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}

        <div className="mt-4 max-h-72 overflow-y-auto rounded-xl border border-[var(--color-border)]">
          {isLoading ? (
            <div className="flex items-center justify-center p-8"><Spinner size="sm" /></div>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-center text-sm text-[var(--color-text-muted)]">
              {organizations.length === 0 ? t('workspace.impersonate.noOrganizations') : t('workspace.impersonate.noMatch')}
            </p>
          ) : (
            filtered.map((organization) => (
              <button
                key={organization.uuid}
                type="button"
                disabled={isSubmitting}
                onClick={() => handlePick(organization)}
                className="cursor-pointer flex w-full items-center justify-between gap-3 border-b border-[var(--color-border)] px-4 py-3 text-left last:border-b-0 hover:bg-[var(--color-bg-muted)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="truncate text-sm font-medium text-[var(--color-text)]">{organization.name}</span>
                <span className="shrink-0 text-xs text-[var(--color-text-muted)]">
                  {t('workspace.impersonate.memberCount', { count: organization.member_count })}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

ImpersonateTenantModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImpersonate: PropTypes.func.isRequired,
  organizations: PropTypes.arrayOf(PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    member_count: PropTypes.number,
  })),
  isLoading: PropTypes.bool,
  isSubmitting: PropTypes.bool,
};
